import React, { useState, useMemo } from 'react';
import { FormattedCommit } from '../types';
import { hasTicketNumbers, getAllTickets, filterCommits } from '../utils/ticketUtils';
import { formatCompactDate, formatTableDate } from '../utils/dateUtils';

interface CommitsTableProps {
  commits: FormattedCommit[];
}

export const CommitsTable: React.FC<CommitsTableProps> = ({ commits }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [ticketFilter, setTicketFilter] = useState('All Tickets');
  const [showAll, setShowAll] = useState(false);
  const [showSummaryTable, setShowSummaryTable] = useState(false);


  const commitsWithTickets = commits.filter(hasTicketNumbers);
  const allTickets = getAllTickets(commitsWithTickets);

  const filteredCommits = useMemo(() => {
    const baseCommits = showAll ? commits : commitsWithTickets;
    return filterCommits(baseCommits, searchTerm, ticketFilter);
  }, [commits, commitsWithTickets, searchTerm, ticketFilter, showAll]);

  const ticketOptions = ['All Tickets', ...allTickets];

  const coverage = commits.length > 0 ? (commitsWithTickets.length / commits.length) * 100 : 0;

  if (!commits || commits.length === 0) {
    return (
      <div style={{ color: '#ff6b35', padding: '20px' }}>
        No commits found for the selected criteria.
      </div>
    );
  }
  
  return (
    <div className="commits-table">
      <h3 style={{ marginBottom: '20px' }}>
        🎫 Commits ({commitsWithTickets.length}/{commits.length} with tickets)
      </h3>

      {/* Search and filter controls */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '2fr 2fr 1fr', 
        gap: '15px', 
        marginBottom: '20px',
        alignItems: 'end'
      }}>
        <div>
          <input
            type="text"
            placeholder="🔍 Search messages, authors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
        </div>

        <div>
          <select
            value={ticketFilter}
            onChange={(e) => setTicketFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          >
            {ticketOptions.map(option => (
              <option key={option} value={option}>
                🎫 {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            Show all
          </label>
          <small style={{ color: '#666', display: 'block', marginTop: '2px' }}>
            Include commits without tickets
          </small>
        </div>
      </div>

      {filteredCommits.length === 0 ? (
        <div style={{ color: '#666', padding: '20px', textAlign: 'center' }}>
          No commits match the current filters.
        </div>
      ) : (
        <>
          {/* Compact commit list */}
          <div className="commits-list">
            {filteredCommits.map((commit, index) => {
              const formattedDate = commit.date ? formatCompactDate(commit.date) : '?';
              const author = commit.author && commit.author.length > 12 ? commit.author.substring(0, 12) + '...' : (commit.author || 'Unknown');

              return (
                <div
                  key={`${commit.sha || 'unknown'}-${index}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 6fr 1.5fr',
                    gap: '15px',
                    padding: '8px 0',
                    borderBottom: index < filteredCommits.length - 1 ? '1px solid #eee' : 'none',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <a
                      href={commit.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#007bff',
                        textDecoration: 'none',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                      }}
                    >
                      {commit.sha || 'unknown'}
                    </a>
                  </div>

                  <div>
                    <div
                      dangerouslySetInnerHTML={{ __html: commit.formatted_message || commit.message || 'No message' }}
                      style={{ fontSize: '14px', lineHeight: '1.4' }}
                    />
                  </div>

                  <div style={{ textAlign: 'right', fontSize: '13px' }}>
                    <div style={{ fontWeight: 'bold' }}>{author}</div>
                    <div style={{ color: '#666' }}>{formattedDate}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Statistics */}
          <div style={{ 
            marginTop: '20px', 
            padding: '10px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            <strong>Stats:</strong> {commits.length} total • {commitsWithTickets.length} with tickets • {coverage.toFixed(0)}% coverage
          </div>

          {/* Summary table (expandable) */}
          <div style={{ marginTop: '20px' }}>
            <button
              onClick={() => setShowSummaryTable(!showSummaryTable)}
              style={{
                background: 'none',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              📊 Summary Table {showSummaryTable ? '▼' : '▶'}
            </button>

            {showSummaryTable && (
              <div style={{ marginTop: '15px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>SHA</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Tickets</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Message</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Author</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>GitHub</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCommits.map((commit, index) => {
                      const tickets = (commit.full_message || commit.message || '').match(/\b(ENG-\d+)\b/gi) || [];
                      const ticketsStr = tickets.join(', ');
                      const message = commit.message && commit.message.length > 60 ? commit.message.substring(0, 60) + '...' : (commit.message || 'No message');
                      const formattedDate = commit.date ? formatTableDate(commit.date) : 'Unknown';

                      return (
                        <tr key={`${commit.sha || 'unknown'}-table-${index}`}>
                          <td style={{ padding: '6px', border: '1px solid #ddd', fontFamily: 'monospace' }}>
                            {commit.sha || 'unknown'}
                          </td>
                          <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                            {ticketsStr}
                          </td>
                          <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                            {message}
                          </td>
                          <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                            {commit.author || 'Unknown'}
                          </td>
                          <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                            {formattedDate}
                          </td>
                          <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                            <a href={commit.url || '#'} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                              GitHub
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
